import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { DropdownItemCheckbox, DropdownItemCheckboxGroup } from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button/new';
import Badge from '@atlaskit/badge';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/core/search';
import PersonIcon from '@atlaskit/icon/core/person';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

const OwnerCheckboxItem = ({
	ownerId,
	displayName,
	isSelected,
	onClick,
}: {
	ownerId: string;
	displayName: string;
	isSelected: boolean;
	onClick: (ownerId: string) => void;
}) => {
	const handleClick = useCallback(() => onClick(ownerId), [onClick, ownerId]);
	return useMemo(
		() => (
			<DropdownItemCheckbox
				key={ownerId}
				id={`owner-${ownerId}`}
				isSelected={isSelected}
				onClick={handleClick}
			>
				{displayName}
			</DropdownItemCheckbox>
		),
		[ownerId, displayName, isSelected, handleClick],
	);
};

const i18n = defineMessages({
	triggerLabel: {
		id: 'performance-insights.content-metrics.filters.content-owner.trigger-label.non-final',
		defaultMessage: 'Content owner',
		description:
			'Label for the Content Owner filter dropdown trigger button in the content metrics view controls bar. Opens a popup to search and filter content by the person who owns it.',
	},
	searchPlaceholder: {
		id: 'performance-insights.content-metrics.filters.content-owner.search-placeholder.non-final',
		defaultMessage: 'Search for a person',
		description:
			'Placeholder text for the search input in the Content Owner filter popup. Allows admins to search for specific people to filter by.',
	},
	searchLabel: {
		id: 'performance-insights.content-metrics.filters.content-owner.search-label.non-final',
		defaultMessage: 'Search people',
		description: 'Accessible label for the search input in the Content Owner filter popup.',
	},
});

const styles = cssMap({
	popupContent: {
		width: '240px',
	},
	searchContainer: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
	},
	listContainer: {
		maxHeight: '240px',
		overflowY: 'auto',
	},
});

export type ContentOwnerOption = {
	id: string;
	displayName: string;
};

export type ContentOwnerFilterProps = {
	owners: ContentOwnerOption[];
	selectedOwnerIds: string[];
	onSelectionChange: (selectedOwnerIds: string[]) => void;
};

export const ContentOwnerFilter = ({
	owners,
	selectedOwnerIds,
	onSelectionChange,
}: ContentOwnerFilterProps) => {
	const { formatMessage } = useIntl();
	const { Popup } = useChoreographerAssets();
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');

	const filteredOwners = useMemo(
		() =>
			owners.filter((owner) => owner.displayName.toLowerCase().includes(searchTerm.toLowerCase())),
		[owners, searchTerm],
	);

	const handleClick = useCallback(
		(ownerId: string) => {
			if (selectedOwnerIds.includes(ownerId)) {
				onSelectionChange(selectedOwnerIds.filter((id) => id !== ownerId));
			} else {
				onSelectionChange([...selectedOwnerIds, ownerId]);
			}
		},
		[selectedOwnerIds, onSelectionChange],
	);

	const handleSearchChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
		setSearchTerm(e.currentTarget.value);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		setSearchTerm('');
	}, []);

	const hasSelections = selectedOwnerIds.length > 0;
	const extraCount = selectedOwnerIds.length - 1;

	const getTriggerLabel = () => {
		if (!hasSelections) {
			return formatMessage(i18n.triggerLabel);
		}
		const firstSelectedOwner = owners.find((o) => o.id === selectedOwnerIds[0]);
		const firstName = firstSelectedOwner?.displayName ?? selectedOwnerIds[0];
		return `${formatMessage(i18n.triggerLabel)}: ${firstName}`;
	};

	return (
		<Popup
			isOpen={isOpen}
			onClose={handleClose}
			placement="bottom-start"
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					ref={triggerProps.ref}
					isSelected={hasSelections || isOpen}
					onClick={() => setIsOpen(!isOpen)}
					iconBefore={(iconProps) => <PersonIcon {...iconProps} label="" />}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} label="" />}
				>
					<Inline alignBlock="center" space="space.050">
						{getTriggerLabel()}
						{extraCount > 0 && <Badge appearance="primary">{`+${extraCount}`}</Badge>}
					</Inline>
				</Button>
			)}
			content={() => (
				<Box xcss={styles.popupContent}>
					<Box xcss={styles.searchContainer}>
						<Textfield
							isCompact
							appearance="none"
							aria-label={formatMessage(i18n.searchLabel)}
							placeholder={formatMessage(i18n.searchPlaceholder)}
							value={searchTerm}
							onChange={handleSearchChange}
							elemBeforeInput={
								<span>
									<SearchIcon spacing="spacious" label="" />
								</span>
							}
							autoFocus
						/>
					</Box>
					<Box xcss={styles.listContainer} role="menu">
						<DropdownItemCheckboxGroup id="content-owner-filter">
							{filteredOwners.map((owner) => (
								<OwnerCheckboxItem
									key={owner.id}
									ownerId={owner.id}
									displayName={owner.displayName}
									isSelected={selectedOwnerIds.includes(owner.id)}
									onClick={handleClick}
								/>
							))}
						</DropdownItemCheckboxGroup>
					</Box>
				</Box>
			)}
		/>
	);
};
